"use client";
import { ICruisesOperators } from "@/interfaces/ICruisesOperators";
import { Box, Button, Typography, Container, Pagination, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { DestinationHero } from "../destinations/DestinationHero";
import { getCruiseOperators } from "@/services/backOffice";
import { PaymentIconsHeaderMobile } from "../common/PaymentHeader";

const CruiseLines: React.FC<{ cruiseLines: ICruisesOperators }> = ({ cruiseLines }) => {
  // const operators = operatorsJson;

  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [operators, setOperators] = useState<ICruisesOperators>(cruiseLines);

  const scrollIntoView = () => {
    const element = document.getElementById("cruise-line");
    if (element) {
      window?.scrollTo({
        top: element.offsetTop - 120,
        behavior: "smooth",
      });
    }
  };
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setIsLoading(true);
    scrollIntoView();
  };
  useEffect(() => {
    const fetchOperater = async () => {
      if (page) {
        try {
          const { data } = await getCruiseOperators(8, page);
          setOperators(data);
          setIsLoading(false);
        } catch (error) {
          console.error("operators data fetcing error", error);
          setIsLoading(false);
        }
      }
    };
    fetchOperater();
  }, [page]);

  return (
    <Box>
      <DestinationHero
        image={`/images/cruise-lines/all-cruise-lines.webp`}
        titletop="Don't just cruise; cruise best!"
        title="Let's discover YOUR ideal Best Cruise Line!"
        content="Cruise lines, halt your never ending quest! Find the Best Cruise Lines that suits you best by looking here.
         We make finding the perfect cruise search easy by providing information on reputable providers and a wide range of itineraries.
        Find the Best Cruise Line that suits your style and budget,whether you dream of Alaskan glaciers or Caribbean beaches.
         Experience the thrill of creating memories that will last a lifetime today! "
      />
      <PaymentIconsHeaderMobile sx={{ mt: "6px" }} />

      <Container>
        <Box id="cruise-line">
          <Box sx={{ mt: "2rem", mb: "2.2rem" }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2.2rem", md: "3rem" },
                fontWeight: { xs: 800, md: 700 },
                textAlign: "center",
                color: "primary.main",
                lineHeight: { xs: 1.1, md: 1.3 },
                mb: { xs: "10px", md: "0px" },
                span: {
                  color: "transparent",
                  WebkitTextStrokeColor: "#CE0000",
                  WebkitTextStrokeWidth: "1px",
                },
              }}
            >
              Explore Our Most <span>Popular Cruise Lines</span>
            </Typography>
            <Box
              sx={{
                fontSize: { xs: "14px", md: "1rem" },
                textAlign: "center",
                fontWeight: 400,
                lineHeight: { xs: 1.4, md: 1.3 },
                mb: { xs: "1.6rem", md: "1.5rem" },
              }}
            >
              With our most popular cruises, you can get exactly what you want. Search cruise lines
              that call at 300+ ports of call and provide hundreds of shore excursions.
            </Box>
          </Box>

          <Grid container spacing={3}>
            {!isLoading ? (
              <>
                {operators?.operators?.map((data, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                    <CruiseLineCard
                      coverImageHref={data?.coverImageHref}
                      profileImageHref={data?.profileImageHref}
                      title={data?.title}
                      id={data?.id}
                      description={data?.description}
                    />
                  </Grid>
                ))}
              </>
            ) : (
              <>
                {[...new Array(8)].map((index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                    <CruiseLineSkeliton />
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </Box>
        <Box sx={{ mt: "2rem" }}>
          <Pagination
            count={
              (operators?.total ?? 0) / 8 > 1 ? Number(((operators?.total ?? 0) / 8).toFixed()) : 1
            }
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            sx={{ width: "fit-content", m: "auto" }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default CruiseLines;

const CruiseLineCard: React.FC<{
  profileImageHref?: string;
  coverImageHref?: string;
  title?: string;
  description?: string;
  id?: string;
}> = ({ profileImageHref, coverImageHref, title, description, id }) => {
  const formtedDiscription =
    description
      ?.replace(/<h1/g, "<h2")
      .replace(/<\/h1>/g, "</h2>")
      .replace(/<h2/g, "<h3")
      .replace(/<\/h2>/g, "</h3>") ?? "";
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "300px", md: "350px" },
        border: "2px solid",
        borderColor: "primary.main",
        boxShadow: "3px 0px 2px rgba(132, 181, 255, 0.3)",
        borderRadius: "15px",
        overflow: "hidden",
        p: "1rem",
        transition: "transform 0.5s ease",
        "&:hover": {
          transform: "scale(1.05)",
          cursor: "pointer",
        },
        img: {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "flex",
        },
      }}
    >
      {coverImageHref && (
        <Image
          src={coverImageHref}
          fill
          alt={`Image ${title}`}
          loader={({ src }) => src}
          style={{ borderRadius: "15px" }}
          unoptimized
        />
      )}

      <Box>
        <Box
          sx={{
            top: "0.5rem",
            right: { xs: "0.5rem", md: "1rem" },
            position: "absolute",
            pointerEvents: "none",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              width: "175px",
              height: "45px",
              bgcolor: "#ffffffc7",
              p: "5px",
              borderRadius: "12px",
            }}
          >
            <Image
              src={profileImageHref ?? ""}
              height={35}
              width={70}
              alt={`Image ${title}`}
              style={{ objectFit: "contain" }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          bottom: 0,
          right: 0,
          position: "absolute",
          bgcolor: "#fff",
          height: "120px",
          width: "100%",
          py: "0.5rem",
          borderRadius: "0px 15px 15px 15px ",
        }}
      >
        <Box
          sx={{
            top: "-30px",
            left: 0,
            position: "absolute",
            py: "5px",
            px: "0.5rem",
            bgcolor: "#fff",
            width: "fit-content",
            borderRadius: "12px 12px 0px 0px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: "1rem", md: "1rem" },
              fontWeight: 400,
              mx: "8px",
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              top: "10px",
              right: "-20px",
              position: "absolute",
              py: "5px",
              px: "0.5rem",
              bgcolor: "transparent",
              width: "20px",
              height: "20px",
              borderRadius: "0px 0px 0px 12px",
              boxShadow: "-6px 6px 0 #fff",
            }}
          ></Box>
        </Box>

        <Typography
          sx={{
            fontSize: { xs: "14px", md: "12px" },
            overflow: "hidden",
            mx: "16px",
            lineHeight: 1.1,
            color: "#000",
            "> p": {
              mt: "2px !important",
            },
            a: {
              color: "#000",
              textTransform: "none",
            },
            strong: {
              color: "#000",
              fontWeight: 600,
              fontSize: { xs: "14px", md: "12px" },
              lineHeight: 1.1,
            },
            WebkitLineClamp: 3,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
          }}
          dangerouslySetInnerHTML={{
            __html: formtedDiscription,
          }}
        />

        <Button
          variant="contained"
          sx={{
            bottom: { xs: "1rem", md: "0.5rem" },
            right: { xs: "1rem", md: "0.5rem" },
            position: "absolute",
            zIndex: 12,
          }}
          onClick={() => (window.location.href = `/best-cruise-lines/${id}`)}
        >
          View Cruises
        </Button>
      </Box>
    </Box>
  );
};

const CruiseLineSkeliton = () => {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "300px", md: "350px" },
        border: "2px solid",
        borderColor: "#aaa",
        borderRadius: "15px",
        overflow: "hidden",
      }}
    >
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{ width: "100%", height: { xs: "300px", md: "350px" }, position: "absolute" }}
      />

      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          top: "1rem",
          right: "1rem",
          position: "absolute",
          width: "175px",
          height: "45px",
          bgcolor: "#ccc",
          borderRadius: "12px",
        }}
      />

      <Box
        sx={{
          bottom: 0,
          right: 0,
          position: "absolute",
          bgcolor: "#fff",
          height: "135px",
          width: "100%",
          py: "0.5rem",
          borderRadius: "0px 15px 15px 15px ",
        }}
      >
        <Box
          sx={{
            top: "-28px",
            left: 0,
            position: "absolute",
            py: "5px",
            px: "0.5rem",
            bgcolor: "#fff",
            width: "fit-content",
            borderRadius: "12px 12px 0px 0px",
          }}
        >
          <Skeleton
            sx={{
              fontSize: "1rem",
              width: "100px",
              borderRadius: "4px",
              mx: "8px",
            }}
            variant="text"
            animation="wave"
          />
        </Box>

        <Box sx={{ p: "1rem" }}>
          <Grid container size={{ xs: 12 }} spacing={0.5}>
            {[...new Array(3)].map((index2) => (
              <Grid key={index2} size={{ xs: 12 }}>
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    height: "10px",
                    borderRadius: "4px",
                  }}
                />
              </Grid>
            ))}
            <Grid size={{ xs: 12 }}>
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  height: "10px",
                  width: "50%",
                  borderRadius: "4px",
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }} mt={2}>
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  borderRadius: "4px",
                  bgcolor: "#aaa",
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
